import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AuthContext, CommonContext, useApi } from '@repo-level/core-front';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { Form, FormApi, FormState, useFormState } from 'informed';

import { register, RegistrationFormApi } from 'api/auth';
import { Button } from 'components/buttons';
import { Checkbox } from 'components/form/Checkbox';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Password } from 'components/form/Password';
import { Select } from 'components/form/Select';
import { ModalDefaultProps } from 'components/Modal';
import { ModalsContext, ModalType } from 'contexts/modal';
import { required, validatePassword } from 'helpers/validators';
import { MAP_COUNTRY_CURRENCY } from 'helpers/conf';
import { AddonsContext } from 'contexts/addons';
import { AnalyticsEventName } from 'api/analytics';
import { useAnalytics } from 'helpers/analytics';
import { PrivacyPolicyModal } from 'components/modals/PrivacyPolicyModal';
import { TermsAndConditionsModal } from 'components/modals/TermsAndConditionsModal';
import { ModalTemplate } from 'components/ModalTemplate';
import { Logo } from 'components/Logo';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { HaveAccountButton } from 'components/HaveAccountButton';
import {
  LocalStorageDeposit,
  localStorageEmail,
  mergeRefs,
} from 'helpers/common';
import { RegistrationBanner } from './components/RegistrationBanner';
import { useFranceLocale } from 'hooks/franceLocale';
import { useLogin } from 'hooks/login';
import { InputEmail } from 'components/form/InputEmail';
import { RegPayments } from '../components/RegPayments';

import { ScFormSubmitGroup } from 'components/form/groups/styled';
import { ScCheckboxesList, ScFieldGroup, ScLink } from './styled';

import background from 'assets/images/backgrounds/modals/registration.jpg';
import { useNavigation } from 'react-navi';
import { getCountryFlagIcon } from 'helpers/cdn';
import { useMedia } from 'modules/common/hooks/useMedia';
import { useIsLanding } from 'modules/promoLandings/hooks/useIsLanding';

export const Registration = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const { t } = useTranslation(['glossary', 'common']);
    const { handleModal } = useContext(ModalsContext);

    const containerRef = useRef<HTMLDivElement>(null);

    const { isMobileS } = useMedia();

    const { isFRWithoutStag } = useFranceLocale();

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? {
            type: 'mobile',
            mode: {
              type: 'background',
              src: background,
            },
            headlineGapSm: true,
          }
        : {
            type: 'desktop',
            size: 'small',
            mode: {
              type: 'background',
              src: background,
            },
            headlineGapSm: true,
          };
    }, [isMobileS]);

    const HaveAccountComponent = useCallback(
      () => (
        <HaveAccountButton
          descr={t('common:auth.registration.loginDescription')}
          btnText={t('login')}
          handleClick={handleModal(ModalType.Login)}
        />
      ),
      [handleModal, t]
    );

    const handleScrollBottom = useCallback(() => {
      if (containerRef.current !== null) {
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }
    }, []);

    if (isFRWithoutStag) {
      handleModal(ModalType.Login)();
      return null;
    }

    return (
      <>
        <ModalTemplate
          ref={mergeRefs(ref, containerRef)}
          onClose={onClose}
          settings={modalSettings}
          title={<Logo height={28} />}
          description={t('sloganReg')}
          header={<RegistrationBanner />}
          footer={<HaveAccountComponent />}
        >
          <RegistrationForm handleScrollBottom={handleScrollBottom} />
        </ModalTemplate>
      </>
    );
  }
);

interface RegistrationFormProps {
  handleScrollBottom: () => void;
}

export const RegistrationForm: FC<RegistrationFormProps> = ({
  handleScrollBottom,
}) => {
  const { t } = useTranslation();
  const { sendEventToAnalytics } = useAnalytics();
  const { navigate } = useNavigation();

  const [isAuthRequestsPending, setIsAuthRequestsPending] = useState(false);
  const [showPromosCheckboxes, setShowPromosCheckboxes] = useState(false);

  const [
    requestRegister,
    { error: registerError, pending: registerPending },
  ] = useApi(register);
  const { handleLogin, loginPending } = useLogin();

  const { insertUser, pendingUser, fetchUser } = useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);
  const { enableCurrencies } = useContext(AddonsContext);

  const formApiRef = useRef<FormApi<RegistrationFormApi>>();

  const { currentIp, countries, setRegBanner } = useContext(CommonContext);

  const { isLanding } = useIsLanding();

  const defaultCurrency = useMemo(() => {
    if (currentIp == null) return;

    return (
      MAP_COUNTRY_CURRENCY[currentIp.countryCode] ||
      MAP_COUNTRY_CURRENCY.default
    );
  }, [currentIp]);

  const currencyOptions = useMemo(
    () =>
      enableCurrencies.map(({ code, symbol, fiat }) => ({
        name: fiat ? code : symbol,
        value: code,
      })),
    [enableCurrencies]
  );

  const errors = useMemo(() => {
    if (
      registerError == null ||
      registerError.errors == null ||
      isAuthRequestsPending
    )
      return [];

    let accError = [];

    if (
      registerError.errors.email != null &&
      registerError.errors.email.taken != null
    ) {
      accError.push({
        field: 'email',
        message: registerError.errors.email.taken,
      });
    }

    if (registerError.errors.profile != null) {
      accError.push({
        field: 'termsAgePromosAcceptance',
        message: t('validation:regCheckbox'),
      });
    }

    if (registerError.errors != null) {
      const l = Object.keys(registerError.errors).map((key) => ({
        field: key,
        message: Object.values(registerError.errors[key]),
      }));

      accError = [...accError, ...l];
    }

    if (registerError.errors.profile != null) {
      const l = Object.keys(registerError.errors.profile).map((key) => ({
        field: key,
        message: Object.values(registerError.errors.profile[key]),
      }));

      accError = [...accError, ...l];
    }

    return accError;
  }, [isAuthRequestsPending, registerError, t]);

  useEffect(() => {
    if (formApiRef.current == null || errors.length === 0) return;

    errors.forEach(({ field, message }) => {
      formApiRef.current?.setError(field, message);
    });
  }, [errors]);

  const handleSubmit = useCallback(
    async ({
      termsAgePromosAcceptance,
      receivePromosMain,
      agreedToPartnerPromotions,
      ...values
    }: RegistrationFormApi) => {
      setIsAuthRequestsPending(true);

      const loginValues = {
        email: values.email,
        password: values.password,
      };

      const { response, error } = await requestRegister({
        ...values,
        ...loginValues,
        ageAcceptance: termsAgePromosAcceptance,
        termsAcceptance: termsAgePromosAcceptance,
        receivePromos: !values.receivePromos,
        receivePromosViaPhoneCalls: !values.receivePromosViaPhoneCalls,
        receiveSmsPromos: !values.receiveSmsPromos,
        agreedToPartnerPromotions: agreedToPartnerPromotions,
      }).request;

      localStorageEmail.push(formApiRef?.current?.getState().values.email);

      if (error == null) {
        await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

        setIsAuthRequestsPending(false);

        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__REGISTRATION__FORM__SUBMIT,
          attributes: {
            email: values.email,
          },
        });

        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__REGISTRATION__FORM__SUCCESS,
          attributes: { additionalData: JSON.stringify(response?.data) },
        });

        if (isLanding) {
          setRegBanner(null);
          navigate('/');
        }

        handleModal(ModalType.Welcome)();
      } else {
        if ((error as any)?.data?.errors.email?.taken) {
          const { response } = await handleLogin(loginValues);

          if (response?.data) {
            await insertUser(response?.data, REASON_FOR_UPDATING_USER.LOGIN);

            isLanding && navigate('/');

            setIsAuthRequestsPending(false);

            handleModal(undefined)();

            return;
          }
        }

        setIsAuthRequestsPending(false);

        sendEventToAnalytics({
          name: AnalyticsEventName.MODAL__REGISTRATION__FORM__ERROR,
          attributes: {
            additionalData: JSON.stringify((error as any)?.data?.errors),
          },
        });
      }
    },
    [
      requestRegister,
      fetchUser,
      sendEventToAnalytics,
      isLanding,
      handleModal,
      setRegBanner,
      navigate,
      handleLogin,
      insertUser,
    ]
  );

  useEffect(() => {
    if (registerError == null || registerError.errors == null) return;

    if (
      registerError.errors.email != null &&
      registerError.errors.email.taken != null
    ) {
      const timeoutId = setTimeout(handleScrollBottom, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [handleScrollBottom, registerError]);

  const removeTermsCheckError = () => {
    formApiRef?.current?.setError('termsAgePromosAcceptance', undefined);
  };

  const depositCurrency = LocalStorageDeposit.get().currency;

  const handlePromoCheckboxesOnChange = useCallback(
    (formState: FormState<RegistrationFormApi>) => {
      const receivePromosMainCheckbox = formState.values?.receivePromosMain;

      setShowPromosCheckboxes(!!receivePromosMainCheckbox);
    },
    []
  );

  useEffect(() => {
    if (!showPromosCheckboxes) {
      formApiRef.current?.setValue('receivePromos', false);
      formApiRef.current?.setValue('receiveSmsPromos', false);
      formApiRef.current?.setValue('receivePromosViaPhoneCalls', false);
    }
  }, [showPromosCheckboxes]);

  const countriesOptions = useMemo(
    () =>
      countries.map(({ name, code }) => ({
        name,
        nameS: name,
        value: code,
        icon: getCountryFlagIcon(code).toLowerCase(),
      })),
    [countries]
  );

  return (
    <Form<RegistrationFormApi>
      getApi={(api) => {
        formApiRef.current = api;
      }}
      onSubmit={handleSubmit}
      onChange={handlePromoCheckboxesOnChange}
    >
      <Analytics />
      <FormGroup>
        <InputEmail />
      </FormGroup>
      <FormGroup>
        <Password
          animatedPlaceholder={t('glossary:password')}
          field="password"
          validate={validatePassword}
          validateOnChange
          validateOnBlur
          strengthChecker
        />
      </FormGroup>
      <FormGroup>
        <ScFieldGroup>
          <Select
            initialValue={currentIp?.countryCode}
            field="country"
            animatedPlaceholder={t('glossary:country')}
            options={countriesOptions}
            validate={required}
            maxHeight={160}
            withBorderMode
            validateOnBlur
            selectedOptionWithoutIcon
            dropBoxShadow
          />
          <Select
            initialValue={depositCurrency ? depositCurrency : defaultCurrency}
            field="currency"
            animatedPlaceholder={t('glossary:currency')}
            options={currencyOptions}
            validate={required}
            maxHeight={160}
            withBorderMode
            validateOnBlur
            selectedOptionWithoutIcon
            dropBoxShadow
          />
        </ScFieldGroup>
      </FormGroup>
      <FormGroup marginBottom={8}>
        <ScCheckboxesList isOpenPromoCheckboxes={showPromosCheckboxes}>
          <Checkbox
            initialValue={!!localStorage.getItem('isStag')}
            label={
              <Trans
                ns="common"
                i18nKey="auth.registration.acceptTermsConditions"
                components={{
                  2: (
                    <ScLink
                      onClick={(e) => {
                        handleModal(TermsAndConditionsModal, {
                          stayInSameWindow: true,
                        })();
                        e.preventDefault();
                      }}
                    />
                  ),
                  5: (
                    <ScLink
                      onClick={(e) => {
                        handleModal(PrivacyPolicyModal, {
                          stayInSameWindow: true,
                        })();
                        e.preventDefault();
                      }}
                    />
                  ),
                }}
              />
            }
            field="termsAgePromosAcceptance"
            onClick={removeTermsCheckError}
          />
          <Checkbox
            label={t('common:auth.registration.offersCheckboxes.refuseOffers')}
            field="receivePromosMain"
            tooltip={t('common:auth.registration.offersCheckboxes.tooltip')}
          />

          <div className="promo-checkboxes">
            <Checkbox
              label={t(
                'common:auth.registration.offersCheckboxes.refuseEmailOffers'
              )}
              field="receivePromos"
            />
            <Checkbox
              label={t(
                'common:auth.registration.offersCheckboxes.refuseSmsOffers'
              )}
              field="receiveSmsPromos"
            />
            <Checkbox
              label={t(
                'common:auth.registration.offersCheckboxes.refusePhoneOffers'
              )}
              field="receivePromosViaPhoneCalls"
            />
          </div>

          <Checkbox
            label={t('common:auth.registration.acceptPartnerPromotions')}
            field="agreedToPartnerPromotions"
          />
        </ScCheckboxesList>
      </FormGroup>
      <ScFormSubmitGroup>
        <Button
          green
          xLarge
          wide
          pending={registerPending || loginPending || pendingUser}
          type="submit"
        >
          {t('glossary:joinNow')}
        </Button>
      </ScFormSubmitGroup>
      <RegPayments marginTop={32} />
    </Form>
  );
};

const Analytics = () => {
  const { touched } = useFormState();
  const touchedForm = Object.values(touched).some((t) => t);
  const { sendEventToAnalytics } = useAnalytics();

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__REGISTRATION__FORM__APPEARANCE,
    });
  }, [sendEventToAnalytics]);

  useEffect(() => {
    if (touchedForm) {
      sendEventToAnalytics({
        name: AnalyticsEventName.MODAL__REGISTRATION__FORM__TOUCHED,
      });
    }
  }, [touchedForm, sendEventToAnalytics]);

  return null;
};
